<script setup lang="ts">
import AlarmReportChart from '@/components/Alarms/Body/Chart/AlarmReportChart.vue'
import type { IAlarmBySensor, IAlarmReportAdherence } from '@/interfaces/stores/alarm'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const props = withDefaults(
  defineProps<{
    adherence: IAlarmReportAdherence
    showSectionHeading?: boolean
  }>(),
  { showSectionHeading: true },
)

const { t } = useI18n()

const classifiedSplit = computed((): IAlarmBySensor[] => [
  {
    sensor_name: t('alarm_report.body.result.adherence.legend.classified'),
    alarm_count: props.adherence.classified_count,
  },
  {
    sensor_name: t('alarm_report.body.result.adherence.legend.unclassified'),
    alarm_count: props.adherence.unclassified_count,
  },
])

const shiftRowsForCharts = computed(() => props.adherence.by_shift.filter((r) => r.total_alarms > 0))

const ratePercent = computed(() => (props.adherence.classification_rate * 100).toFixed(1))
const targetPercent = computed(() => props.adherence.target_classification_percent.toFixed(1))
const slaPercent = computed(() =>
  props.adherence.sla_compliance_rate != null
    ? (props.adherence.sla_compliance_rate * 100).toFixed(1)
    : null,
)
</script>

<template>
  <div class="flex flex-col gap-6 border border-stroke bg-gray-50 p-4 lg:p-6">
    <div v-if="showSectionHeading">
      <h2 class="font-satoshi text-title-sm font-bold text-black">
        {{ t('alarm_report.body.result.adherence.section_title') }}
      </h2>
      <p class="mt-1 max-w-prose text-title-xsm font-medium text-gray">
        {{ t('alarm_report.body.result.adherence.scope_hint') }}
      </p>
    </div>
    <p v-else class="max-w-prose text-title-xsm4 text-gray">
      {{ t('alarm_report.body.result.adherence.scope_hint') }}
    </p>

    <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <div class="flex flex-col bg-white p-4 shadow-md">
        <p class="text-title-xsm font-medium text-gray">{{ t('alarm_report.body.result.adherence.cards.base_total') }}</p>
        <p class="pt-2 text-title-md font-bold text-black">{{ adherence.total_alarms }}</p>
        <p class="text-title-xsm text-gray">
          {{ adherence.classified_count }} {{ t('alarm_report.body.result.adherence.cards.classified_short') }} ·
          {{ adherence.unclassified_count }} {{ t('alarm_report.body.result.adherence.cards.unclassified_short') }}
        </p>
      </div>

      <div class="flex flex-col bg-white p-4 shadow-md lg:col-span-2">
        <p class="text-title-xsm font-medium text-gray">{{ t('alarm_report.body.result.adherence.cards.classification_rate') }}</p>
        <p class="pt-2 text-title-md font-bold text-black">{{ ratePercent }}%</p>
        <p class="text-title-xsm text-gray">
          {{ t('alarm_report.body.result.adherence.cards.target') }}: {{ targetPercent }}%
          —
          {{
            adherence.meets_classification_target
              ? t('alarm_report.body.result.adherence.cards.meets_target_yes')
              : t('alarm_report.body.result.adherence.cards.meets_target_no')
          }}
        </p>
        <div class="relative mt-3 h-3 w-full bg-gray-200">
          <div
            class="h-3 bg-primary"
            :style="{ width: `${Math.min(100, Number.parseFloat(ratePercent))}%` }" />
          <div
            class="absolute top-0 z-10 h-3 w-px bg-black"
            :style="{ left: `${Math.min(100, Number.parseFloat(targetPercent))}%` }" />
        </div>
        <p class="mt-1 text-title-xsm4 text-gray">{{ t('alarm_report.body.result.adherence.cards.progress_hint') }}</p>
      </div>

      <div class="flex flex-col bg-white p-4 shadow-md">
        <p class="text-title-xsm font-medium text-gray">{{ t('alarm_report.body.result.adherence.cards.avg_hours') }}</p>
        <p class="pt-2 text-title-md font-bold text-black">
          {{
            adherence.average_hours_to_classify != null
              ? `${adherence.average_hours_to_classify} h`
              : t('alarm_report.body.result.adherence.cards.no_timing')
          }}
        </p>
        <p class="text-title-xsm text-gray">
          {{ t('alarm_report.body.result.adherence.cards.sla_label') }}: {{ adherence.sla_hours }} h
        </p>
      </div>

      <div class="flex flex-col bg-white p-4 shadow-md lg:col-span-4">
        <p class="text-title-xsm font-medium text-gray">{{ t('alarm_report.body.result.adherence.cards.sla_compliance') }}</p>
        <p class="pt-2 text-title-md font-bold text-black">
          <template v-if="slaPercent != null">
            {{ slaPercent }}% ({{ adherence.classified_within_sla_count }} /
            {{ adherence.classified_with_timing_count }})
          </template>
          <template v-else>
            {{ t('alarm_report.body.result.adherence.cards.no_timing') }}
          </template>
        </p>
      </div>
    </div>

    <div class="grid grid-cols-1 items-stretch gap-5 md:grid-cols-2">
      <AlarmReportChart
        :chart-title="t('alarm_report.body.result.charts.adherence_classified_split.title')"
        chart-type="alarm-report-by-sensor-chart"
        :readings="classifiedSplit"
      />

      <AlarmReportChart
        v-if="shiftRowsForCharts.length"
        :chart-title="t('alarm_report.body.result.charts.adherence_shift_rate.title')"
        chart-type="alarm-adherence-shift-classification-rate-bar"
        :readings="shiftRowsForCharts"
      />

      <AlarmReportChart
        v-if="shiftRowsForCharts.length"
        :chart-title="t('alarm_report.body.result.charts.adherence_shift_hours.title')"
        chart-type="alarm-adherence-shift-avg-hours-bar"
        :readings="shiftRowsForCharts"
      />

      <AlarmReportChart
        v-if="shiftRowsForCharts.length"
        :chart-title="t('alarm_report.body.result.charts.adherence_shift_sla.title')"
        chart-type="alarm-adherence-shift-sla-bar"
        :readings="shiftRowsForCharts"
      />
    </div>
  </div>
</template>
